import {
  createLaneShellPair as createLaneShellPairOp,
  triggerLaneEnrichment as triggerLaneEnrichmentOp,
} from '@gql/operations'
import { type GqlExecutor } from '@hopdrive/sdk-core'
import { LaneValidationError, DuplicateLocationError } from '../../errors/lane-errors'
import { CreateLaneShellInputSchema } from '../../validation/lane-schemas'
import type { LanePair, CreateLaneShellInput } from '../../types'

interface LaneShellResult {
  id: number
  active: number
  status: string
  origin_location_id: number
  destination_location_id: number
}

export interface CreateLaneShellParams extends CreateLaneShellInput {
  executor?: GqlExecutor
}

/**
 * Create a lane shell pair (forward + inverse).
 *
 * @see lane-lifecycle.md#two-step-mutation-pattern
 */
export async function createLaneShell(params: CreateLaneShellParams): Promise<LanePair> {
  const parseResult = CreateLaneShellInputSchema.safeParse(params)
  if (!parseResult.success) {
    throw new LaneValidationError(parseResult.error)
  }

  if (params.originLocationId === params.destinationLocationId) {
    throw new DuplicateLocationError(params.originLocationId)
  }

  // Step 1: INSERT both lanes with status='new'
  const insertResult = await createLaneShellPairOp(
    {
      forwardLane: {
        customer_id: params.customerId,
        origin_location_id: params.originLocationId,
        destination_location_id: params.destinationLocationId,
        description: params.description,
        status: 'new',
        active: 0,
      },
      inverseLane: {
        customer_id: params.customerId,
        origin_location_id: params.destinationLocationId,
        destination_location_id: params.originLocationId,
        description: params.description ? reverseDescription(params.description) : undefined,
        status: 'new',
        active: 0,
      },
    },
    { executor: params.executor }
  )

  const forwardLane = insertResult.forward as LaneShellResult
  const inverseLane = insertResult.inverse as LaneShellResult

  // NOTE: Inverse relationship is computed by Hasura via manual config
  // (swaps origin/destination location IDs), so no explicit linking needed.

  // Step 2: UPDATE status to 'pending' to trigger enrichment
  await triggerLaneEnrichmentOp(
    { ids: [forwardLane.id, inverseLane.id] },
    { executor: params.executor }
  )

  return {
    forward: { ...forwardLane, status: 'pending' } as LanePair['forward'],
    inverse: { ...inverseLane, status: 'pending' } as LanePair['inverse'],
  }
}

function reverseDescription(description: string): string {
  const parts = description.split(' to ')
  return parts.length === 2 ? `${parts[1]} to ${parts[0]}` : description
}
